import { CloseOutlined } from '@ant-design/icons';
import { Button, TreeSelect } from 'antd';
import React from 'react';
import styled from 'styled-components';
import FilePreviewPanel from '../FolderPreview/FilePreviewPanel';
import type { IFolderNode } from '../FolderPreview/utils';
import type { IPaneState, TreeSelectNode } from './utils';

interface IComparisonPaneProps {
  pane: IPaneState;
  treeSelectData: TreeSelectNode[];
  nodeMap: Map<string, IFolderNode>;
  canRemove: boolean;
  onSelectFile: (fileId: string | null) => void;
  onRemove: () => void;
  modelInstanceId?: string;
  selectFilePlaceholder?: string;
  downloadButtonText?: string;
  previewButtonNotAvailableText?: string;
  emptyStateText?: string;
  shouldShowDownload?: boolean;
}

const ComparisonPane: React.FC<IComparisonPaneProps> = ({
  pane,
  treeSelectData,
  nodeMap,
  canRemove,
  onSelectFile,
  onRemove,
  modelInstanceId,
  selectFilePlaceholder = 'Select a file',
  downloadButtonText,
  previewButtonNotAvailableText,
  emptyStateText = 'Select a file to preview',
  shouldShowDownload,
}) => {
  const selectedFile = pane.selectedFileId
    ? (nodeMap.get(pane.selectedFileId) ?? null)
    : null;

  return (
    <PaneWrapper>
      <PaneHeader>
        <TreeSelect
          treeData={treeSelectData}
          value={pane.selectedFileId ?? undefined}
          onChange={val => onSelectFile(val ?? null)}
          placeholder={selectFilePlaceholder}
          treeDefaultExpandAll
          style={{ flex: 1, minWidth: 0 }}
          size="small"
          showSearch
          treeNodeFilterProp="title"
        />
        {canRemove && (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onRemove}
          />
        )}
      </PaneHeader>
      <PaneBody>
        {selectedFile ? (
          <FilePreviewPanel
            file={selectedFile}
            modelInstanceId={modelInstanceId}
            downloadButtonText={downloadButtonText}
            previewButtonNotAvailableText={previewButtonNotAvailableText}
            shouldShowDownload={shouldShowDownload}
            shouldShowFullSize={false}
          />
        ) : (
          <EmptyState>{emptyStateText}</EmptyState>
        )}
      </PaneBody>
    </PaneWrapper>
  );
};

export default ComparisonPane;

const PaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
  min-height: 40px;
`;

const PaneBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;
